import { attachMenuSounds } from './MenuSounds';
import type { RaceMode } from '../game/DrivingAssists';
import { getHomeworldBadge, homeworldBadgeStyle } from './HomeworldBadges';
import { formatHandle } from '../server/handles';

export type PilotAccountStatus = 'guest' | 'linked' | 'claimed';
export type RivalDifficulty = 'balanced' | 'aggressive' | 'timid';

export interface TitleHandlers {
  onRace: (mode: RaceMode, options?: { opponent?: boolean; opponentDifficulty?: RivalDifficulty }) => void;
  onLeaderboard: () => void;
  onClaim: () => void;
  onSettings: () => void;
  onReroll: () => void;
}

const TITLE_SCREEN_ID = 'title-screen';
const TITLE_SCREEN_MARKUP = `
  <div class="tst-card">
    <div class="tst-title">TWIN SUNS TURBO</div>
    <div class="tst-subtitle">Autodrome Control — Time Trial</div>
    <div class="tst-pilot-panel">
      <div class="tst-pilot-topline">
        <span>Pilot Profile</span>
        <span class="tst-pilot-meta">
          <button data-action="reroll" class="tst-pilot-reroll" type="button" aria-label="Roll new pilot name and homeworld" title="Roll new pilot">🎲</button>
          <span class="tst-pilot-status">Guest Pilot</span>
        </span>
      </div>
      <button data-action="claim" class="tst-pilot-button" type="button">
        <span class="tst-homeworld-icon tst-homeworld-icon-title" style="${homeworldBadgeStyle('Tatoona')}" aria-hidden="true"></span>
        <span class="tst-pilot-copy">
          <span class="tst-handle">...</span>
          <span class="tst-homeworld">Homeworld pending</span>
        </span>
      </button>
    </div>
    <div class="tst-status" role="status" aria-live="polite" hidden></div>
    <div class="tst-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" hidden>
      <div class="tst-progress-bar"><div class="tst-progress-fill"></div></div>
      <div class="tst-progress-label"></div>
    </div>
    <label class="tst-rival-difficulty" data-rival-difficulty hidden>
      <span>Rival Difficulty</span>
      <select data-action="opponent-difficulty" aria-label="Rival difficulty">
        <option value="balanced">Balanced</option>
        <option value="aggressive">Aggressive</option>
        <option value="timid">Timid</option>
      </select>
    </label>
    <div class="tst-buttons">
      <button data-action="race" class="tst-btn tst-btn-primary" type="button">Launch Run</button>
      <button data-action="opponent-race" class="tst-btn tst-btn-primary tst-btn-rival" type="button" disabled hidden>Race Rival</button>
      <button data-action="beginner-race" class="tst-btn tst-btn-primary tst-btn-rookie" type="button">Rookie Run</button>
      <button data-action="lb" class="tst-btn" type="button">Timing Board</button>
      <button data-action="settings" class="tst-btn tst-btn-ghost" type="button">Systems</button>
    </div>
    <div class="tst-hint" data-hint-keyboard>WASD / Arrows · Space brake · C view · R reset · Esc pause</div>
    <div class="tst-hint" data-hint-touch>Tap &#9664; &#9654; to steer · Go / Stop · Pause top-right</div>
  </div>
`;

export class TitleScreen {
  readonly root: HTMLDivElement;
  private readonly handleEl: HTMLSpanElement;
  private readonly homeworldEl: HTMLSpanElement;
  private readonly homeworldBadgeEl: HTMLSpanElement;
  private readonly pilotStatusEl: HTMLSpanElement;
  private readonly rerollButton: HTMLButtonElement;
  private readonly statusEl: HTMLDivElement;
  private readonly progressEl: HTMLDivElement;
  private readonly progressFillEl: HTMLElement;
  private readonly progressLabelEl: HTMLDivElement;
  private readonly raceButton: HTMLButtonElement;
  private readonly opponentRaceButton: HTMLButtonElement;
  private readonly beginnerButton: HTMLButtonElement;
  private readonly opponentDifficultyField: HTMLElement;
  private readonly opponentDifficultySelect: HTMLSelectElement;

  constructor(parent: HTMLElement, handlers: TitleHandlers) {
    this.root = resolveTitleRoot(parent);
    ensureOpponentRaceButton(this.root);
    this.handleEl = requiredElement<HTMLSpanElement>(this.root, '.tst-handle');
    this.homeworldEl = requiredElement<HTMLSpanElement>(this.root, '.tst-homeworld');
    this.homeworldBadgeEl = requiredElement<HTMLSpanElement>(this.root, '.tst-homeworld-icon-title');
    this.pilotStatusEl = requiredElement<HTMLSpanElement>(this.root, '.tst-pilot-status');
    this.rerollButton = requiredElement<HTMLButtonElement>(this.root, '[data-action="reroll"]');
    this.statusEl = requiredElement<HTMLDivElement>(this.root, '.tst-status');
    this.progressEl = requiredElement<HTMLDivElement>(this.root, '.tst-progress');
    this.progressFillEl = requiredElement<HTMLElement>(this.root, '.tst-progress-fill');
    this.progressLabelEl = requiredElement<HTMLDivElement>(this.root, '.tst-progress-label');
    this.opponentDifficultyField = requiredElement<HTMLElement>(this.root, '[data-rival-difficulty]');
    this.opponentDifficultySelect = requiredElement<HTMLSelectElement>(this.root, '[data-action="opponent-difficulty"]');
    this.raceButton = requiredElement<HTMLButtonElement>(this.root, '[data-action="race"]');
    this.opponentRaceButton = requiredElement<HTMLButtonElement>(this.root, '[data-action="opponent-race"]');
    this.beginnerButton = requiredElement<HTMLButtonElement>(this.root, '[data-action="beginner-race"]');

    this.raceButton.addEventListener('click', () => handlers.onRace('standard'));
    this.opponentRaceButton.addEventListener('click', () => handlers.onRace('standard', {
      opponent: true,
      opponentDifficulty: this.opponentDifficulty(),
    }));
    this.beginnerButton.addEventListener('click', () => handlers.onRace('beginner'));
    requiredElement<HTMLButtonElement>(this.root, '[data-action="lb"]').addEventListener('click', handlers.onLeaderboard);
    requiredElement<HTMLButtonElement>(this.root, '[data-action="claim"]').addEventListener('click', handlers.onClaim);
    requiredElement<HTMLButtonElement>(this.root, '[data-action="settings"]').addEventListener('click', handlers.onSettings);
    this.rerollButton.addEventListener('click', (e) => {
      e.stopPropagation();
      handlers.onReroll();
    });
    attachMenuSounds(this.root);
  }

  setHandle(handle: string): void {
    this.handleEl.textContent = formatHandle(handle);
  }

  setPilot(handle: string, homeworld: string, accountStatus: PilotAccountStatus | boolean): void {
    const status = normalizePilotAccountStatus(accountStatus);
    const badge = getHomeworldBadge(homeworld);
    this.handleEl.textContent = formatHandle(handle);
    this.homeworldEl.textContent = homeworld === 'Homeworld pending' ? homeworld : `From ${homeworld}`;
    this.homeworldBadgeEl.setAttribute('style', homeworldBadgeStyle(homeworld));
    this.homeworldBadgeEl.dataset.homeworld = badge.slug;
    this.pilotStatusEl.textContent = pilotStatusLabel(status);
    // Re-roll only makes sense for guests — claimed/linked pilots already
    // chose their handle, so hide the dice for them.
    this.rerollButton.hidden = status !== 'guest';
  }

  setProgress(progress: { step: number; total: number; label?: string } | null): void {
    if (!progress || progress.total <= 0) {
      this.progressEl.hidden = true;
      this.progressEl.setAttribute('aria-valuenow', '0');
      this.progressFillEl.style.width = '0%';
      this.progressLabelEl.textContent = '';
      return;
    }
    const step = Math.max(0, Math.min(progress.step, progress.total));
    const pct = Math.round((step / progress.total) * 100);
    this.progressEl.hidden = false;
    this.progressEl.setAttribute('aria-valuenow', String(pct));
    this.progressFillEl.style.width = `${pct}%`;
    this.progressLabelEl.textContent = progress.label ?? '';
  }

  /**
   * Toggle the cinematic backdrop. Off (default) = opaque overlay used
   * while boot is in progress and the render loop hasn't started yet,
   * so the player doesn't see a soft overlay over a black canvas. On =
   * lighter overlay revealing the live race scene; switch on once the
   * loop is running and shaders are warm.
   */
  setCinematic(enabled: boolean): void {
    if (enabled) this.root.dataset.cinematic = 'on';
    else delete this.root.dataset.cinematic;
  }

  setStatus(message: string | null): void {
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
    } else {
      this.statusEl.hidden = false;
      this.statusEl.textContent = message;
    }
  }

  setRaceEnabled(enabled: boolean): void {
    this.raceButton.disabled = !enabled;
    this.opponentRaceButton.disabled = !enabled || this.opponentRaceButton.hidden;
    this.beginnerButton.disabled = !enabled;
  }

  setOpponentRaceVisible(visible: boolean): void {
    this.opponentDifficultyField.hidden = !visible;
    this.opponentRaceButton.hidden = !visible;
    this.opponentRaceButton.disabled = !visible || this.raceButton.disabled;
  }

  setOpponentDifficulty(difficulty: RivalDifficulty): void {
    this.opponentDifficultySelect.value = difficulty;
  }

  private opponentDifficulty(): RivalDifficulty {
    return normalizeRivalDifficulty(this.opponentDifficultySelect.value);
  }

  show(): void { this.root.hidden = false; }
  hide(): void { this.root.hidden = true; }
}

function resolveTitleRoot(parent: HTMLElement): HTMLDivElement {
  const existing = parent.querySelector<HTMLDivElement>(`#${TITLE_SCREEN_ID}`);
  const root = existing ?? document.createElement('div');
  root.id = TITLE_SCREEN_ID;
  root.className = 'tst-screen tst-overlay';
  root.style.zIndex = '25';
  root.hidden = false;
  if (!hasTitleShell(root)) root.innerHTML = TITLE_SCREEN_MARKUP;
  if (!existing) parent.appendChild(root);
  return root;
}

function ensureOpponentRaceButton(root: HTMLElement): void {
  ensureOpponentDifficulty(root);
  if (root.querySelector('[data-action="opponent-race"]')) return;
  const raceButton = root.querySelector('[data-action="race"]');
  if (!raceButton?.parentElement) return;
  const button = document.createElement('button');
  button.dataset.action = 'opponent-race';
  button.className = 'tst-btn tst-btn-primary tst-btn-rival';
  button.type = 'button';
  button.hidden = true;
  button.disabled = true;
  button.textContent = 'Race Rival';
  raceButton.insertAdjacentElement('afterend', button);
}

function ensureOpponentDifficulty(root: HTMLElement): void {
  if (root.querySelector('[data-rival-difficulty]')) return;
  const buttons = root.querySelector('.tst-buttons');
  if (!buttons?.parentElement) return;
  const field = document.createElement('label');
  field.className = 'tst-rival-difficulty';
  field.dataset.rivalDifficulty = '';
  field.hidden = true;
  field.innerHTML = [
    '<span>Rival Difficulty</span>',
    '<select data-action="opponent-difficulty" aria-label="Rival difficulty">',
    '<option value="balanced">Balanced</option>',
    '<option value="aggressive">Aggressive</option>',
    '<option value="timid">Timid</option>',
    '</select>',
  ].join('');
  buttons.insertAdjacentElement('beforebegin', field);
}

function hasTitleShell(root: HTMLElement): boolean {
  // Any new required selector MUST be added here. A pre-rendered shell in
  // index.html that's missing a selector listed here would otherwise pass
  // this check, skip the markup re-injection, and then crash in
  // `requiredElement(...)`. Production "Init failed" regressions live and
  // die by this list.
  return [
    '.tst-card',
    '.tst-handle',
    '.tst-homeworld',
    '.tst-homeworld-icon-title',
    '.tst-pilot-status',
    '.tst-status',
    '.tst-progress',
    '.tst-progress-fill',
    '.tst-progress-label',
    '[data-rival-difficulty]',
    '[data-action="opponent-difficulty"]',
    '[data-action="reroll"]',
    '[data-action="claim"]',
    '[data-action="race"]',
    '[data-action="beginner-race"]',
    '[data-action="lb"]',
    '[data-action="settings"]',
  ].every((selector) => root.querySelector(selector));
}

function isRivalDifficulty(value: string): value is RivalDifficulty {
  return value === 'balanced' || value === 'aggressive' || value === 'timid';
}

export function normalizeRivalDifficulty(value: string): RivalDifficulty {
  return isRivalDifficulty(value) ? value : 'balanced';
}

function requiredElement<T extends Element>(root: HTMLElement, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`TitleScreen is missing ${selector}`);
  return element;
}

function normalizePilotAccountStatus(status: PilotAccountStatus | boolean): PilotAccountStatus {
  if (typeof status === 'boolean') return status ? 'claimed' : 'guest';
  return status;
}

function pilotStatusLabel(status: PilotAccountStatus): string {
  switch (status) {
    case 'claimed':
      return 'Claimed Pilot';
    case 'linked':
      return 'Linked Pilot';
    case 'guest':
    default:
      return 'Guest Pilot';
  }
}
